import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Stack,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Button
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { QuestionTypeSelect } from './QuestionTypeSelect';
import { QUESTION_TYPES } from './CreateQuestionDialog';
import { Question, QuestionType } from '../../types';

interface Option {
  front_text?: string;
  back_text?: string;
  text?: string; // Used for compatibility with form inputs
  value?: number;
  is_other?: boolean;
}

export interface QuestionData {
  text: string;
  category: string;
  question_type: QuestionType;
  low_text: string;
  high_text: string;
  options: Option[];
  video_url?: string;
  visual_component_url?: string;
  key?: string;
}

interface QuestionEditorProps {
  question: QuestionData;
  onChange: (updatedQuestion: QuestionData) => void;
  showCategory?: boolean;
  disableQuestionType?: boolean;
}

export const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  onChange,
  showCategory = true,
  disableQuestionType = false,
}) => {
  const handleChange = (updates: Partial<QuestionData>) => {
    onChange({ ...question, ...updates });
  };

  const handleOptionChange = (index: number, updates: Partial<Option>) => {
    const newOptions = [...question.options];
    
    // Handle different option formats (text vs front_text/back_text)
    if ('text' in updates && updates.text !== undefined) {
      // If text is being updated, also update front_text for compatibility
      newOptions[index] = { 
        ...newOptions[index], 
        ...updates,
        front_text: updates.text  // Ensure front_text is updated when text changes
      };
    } else {
      // For other updates (like front_text or back_text)
      newOptions[index] = { ...newOptions[index], ...updates };
    }
    
    handleChange({ options: newOptions });
  };

  const handleAddOption = () => {
    const newOption = question.question_type === QUESTION_TYPES.MULTIPLE_REVEAL
      ? { front_text: '', back_text: '' }
      : question.question_type === QUESTION_TYPES.STYLED_BOOLEAN
      ? { front_text: 'True', back_text: '' }
      : { front_text: '', is_other: false };
    handleChange({ options: [...question.options, newOption] });
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = question.options.filter((_, i) => i !== index);
    handleChange({ options: newOptions });
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const newType = event.target.value as QuestionType;
    
    // Initialize options for specific question types
    let initialOptions: Option[] = [];
    if (newType === QUESTION_TYPES.STYLED_BOOLEAN) {
      initialOptions = [
        { front_text: 'True', back_text: '' },
        { front_text: 'False', back_text: '' }
      ];
    }
    
    handleChange({
      question_type: newType,
      options: initialOptions
    });
  };

  const renderOptionFields = () => {
    if (!question.options) return null;

    return (
      <Stack spacing={2}>
        {question.options.map((option, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            {question.question_type === QUESTION_TYPES.MULTIPLE_REVEAL ? (
              <>
                <TextField
                  label="Front Text"
                  fullWidth
                  value={option.front_text || ''}
                  onChange={(e) => handleOptionChange(index, { front_text: e.target.value })}
                />
                <TextField
                  label="Back Text"
                  fullWidth
                  value={option.back_text || ''}
                  onChange={(e) => handleOptionChange(index, { back_text: e.target.value })}
                />
              </>
            ) : question.question_type === QUESTION_TYPES.DRAG_AND_DROP ? (
              <TextField
                label="Option Text"
                fullWidth
                value={option.front_text || ''}
                onChange={(e) => handleOptionChange(index, { front_text: e.target.value })}
              />
            ) : question.question_type === QUESTION_TYPES.HORIZONTAL_DRAG_DROP ? (
              <>
                <TextField
                  label="Option Text"
                  fullWidth
                  value={option.front_text || ''}
                  onChange={(e) => handleOptionChange(index, { front_text: e.target.value })}
                />
                <TextField
                  label="Position (left/right)"
                  select
                  value={option.back_text || 'left'}
                  onChange={(e) => handleOptionChange(index, { back_text: e.target.value })}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </TextField>
              </>
            ) : question.question_type === QUESTION_TYPES.STYLED_BOOLEAN ? (
              <TextField
                label={index === 0 ? "True Option Text" : "False Option Text"}
                fullWidth
                value={option.front_text || (index === 0 ? 'True' : 'False')}
                onChange={(e) => handleOptionChange(index, { front_text: e.target.value })}
              />
            ) : (
              <>
                <TextField
                  label="Option Text"
                  fullWidth
                  value={option.front_text || ''}
                  onChange={(e) => handleOptionChange(index, { front_text: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={option.is_other || false}
                      onChange={(e) => handleOptionChange(index, { is_other: e.target.checked })}
                    />
                  }
                  label="Other"
                />
              </>
            )}
            <IconButton onClick={() => handleRemoveOption(index)} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={handleAddOption}>
            Add Option
          </Button>
        </Box>
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Question Text"
        fullWidth
        multiline
        rows={3}
        value={question.text}
        onChange={(e) => handleChange({ text: e.target.value })}
      />
      {showCategory && (
        <TextField
          label="Category"
          fullWidth
          value={question.category}
          onChange={(e) => handleChange({ category: e.target.value })}
        />
      )}
      <TextField
        label="Question Key"
        fullWidth
        value={question.key || ''}
        onChange={(e) => handleChange({ key: e.target.value })}
        helperText="A unique identifier for this question (optional)"
      />
      <QuestionTypeSelect
        value={question.question_type}
        onChange={handleTypeChange}
        disabled={disableQuestionType}
      />

      {question.question_type === QUESTION_TYPES.RATING && (
        <>
          <TextField
            fullWidth
            label="Low Text"
            value={question.low_text}
            onChange={(e) => handleChange({ low_text: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="High Text"
            value={question.high_text}
            onChange={(e) => handleChange({ high_text: e.target.value })}
            margin="normal"
          />
        </>
      )}

      {question.question_type === QUESTION_TYPES.HORIZONTAL_DRAG_DROP && (
        <>
          <TextField
            fullWidth
            label="Left Column Title"
            value={question.low_text || ''}
            onChange={(e) => handleChange({ low_text: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Right Column Title"
            value={question.high_text || ''}
            onChange={(e) => handleChange({ high_text: e.target.value })}
            margin="normal"
          />
        </>
      )}

      {question.question_type === QUESTION_TYPES.VIDEO_PLAYER && (
        <TextField
          fullWidth
          label="Video URL"
          value={question.video_url || ''}
          onChange={(e) => handleChange({ video_url: e.target.value })}
          margin="normal"
          helperText="Enter the video filename from the assets folder"
        />
      )}

      <TextField
        fullWidth
        label="Image URL"
        value={question.visual_component_url || ''}
        onChange={(e) => handleChange({ visual_component_url: e.target.value })}
        margin="normal"
        helperText="Enter the image filename from the assets folder"
      />

      {(() => {
        switch (question.question_type) {
          case QUESTION_TYPES.MULTIPLE_CHOICE:
          case QUESTION_TYPES.MULTIPLE_SELECT:
          case QUESTION_TYPES.MULTIPLE_REVEAL:
          case QUESTION_TYPES.DRAG_AND_DROP:
          case QUESTION_TYPES.HORIZONTAL_DRAG_DROP:
          case QUESTION_TYPES.STYLED_BOOLEAN:
            return renderOptionFields();
          default:
            return null;
        }
      })()}
    </Stack>
  );
};

export default QuestionEditor;
